import TypeBadge from './TypeBadge'

const SCOPE_CLASSES = {
  shared:   "bg-emerald-900/40 text-emerald-400 border border-emerald-800/50",
  personal: "bg-violet-900/40 text-violet-400 border border-violet-800/50",
  project:  "bg-zinc-800/60 text-blue-400 border border-zinc-700/50",
}

function ScopeBadge({ scope }) {
  if (!scope) return null
  const cls = SCOPE_CLASSES[scope] ?? "bg-zinc-800/60 text-zinc-400 border border-zinc-700/50"
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-mono ${cls}`}>
      {scope}
    </span>
  )
}

function highlight(text, query) {
  if (!query) return text
  const terms = query.trim().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return text
  const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi')
  const parts = text.split(pattern)
  return parts.map((part, i) =>
    pattern.test(part)
      ? <mark key={i} className="bg-yellow-400/20 text-yellow-200 rounded px-0.5">{part}</mark>
      : part
  )
}

function timeAgo(isoString) {
  const diff   = Date.now() - new Date(isoString).getTime()
  const mins   = Math.floor(diff / 60000)
  const hours  = Math.floor(diff / 3600000)
  const days   = Math.floor(diff / 86400000)
  const weeks  = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  if (mins < 60)   return `${mins}m ago`
  if (hours < 24)  return `${hours}h ago`
  if (days < 7)    return `${days}d ago`
  if (weeks < 5)   return `${weeks}w ago`
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

export default function MemoryCard({ memory, query, onSelect }) {
  const snippet    = extractSnippet(memory.content)
  const isUpserted = memory.topic_key && memory.updated_at && memory.updated_at !== memory.created_at
  const label      = isUpserted ? "updated" : "saved"
  const dateToShow = isUpserted ? memory.updated_at : memory.created_at
  const expiry     = expiryStatus(memory.expires_at)

  return (
    <button
      onClick={() => onSelect ? onSelect(memory) : null}
      className="w-full text-left p-2.5 border border-zinc-700/60 rounded-lg hover:border-emerald-800/50 hover:bg-zinc-800/50 hover:shadow-[inset_2px_0_0_0_theme(colors.emerald.700/30)] transition-all"
    >
      <div className="flex items-start gap-2 mb-1">
        <TypeBadge type={memory.memory_type} />
        {memory.memory_type === 'synthesis' && memory.synthesis_depth != null && (
          <span className="inline-block px-1.5 py-0.5 rounded text-xs font-mono bg-orange-950/60 text-orange-400/70 border border-orange-900/40">
            depth {memory.synthesis_depth}
          </span>
        )}
        <ScopeBadge scope={memory.scope} />
        {memory.archived_at && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-mono bg-zinc-800 text-zinc-500 border border-zinc-700">
            archived
          </span>
        )}
        {memory.topic_key && (
          <span className="text-[10px] font-mono text-zinc-500/60 truncate">{memory.topic_key}</span>
        )}
      </div>
      <p className="text-sm font-mono text-zinc-100 font-semibold leading-snug mb-0.5">
        {memory.title}
      </p>
      <p className="text-xs font-mono text-zinc-500 leading-relaxed line-clamp-1">
        {highlight(snippet, query)}
      </p>
      <p className="text-[11px] font-mono text-zinc-500 mt-1 flex items-center gap-2">
        <span className="text-zinc-500 text-xs font-mono shrink-0" title={dateToShow}>
          {label} {timeAgo(dateToShow)}
        </span>
        {expiry === 'red' && (
          <span className="text-[10px] font-mono text-red-400">
            expires in {Math.ceil((new Date(memory.expires_at) - Date.now()) / 86400000)}d
          </span>
        )}
        {expiry === 'yellow' && (
          <span className="text-[10px] font-mono text-amber-400">
            expires in {Math.ceil((new Date(memory.expires_at) - Date.now()) / 86400000)}d
          </span>
        )}
      </p>
    </button>
  )
}

function expiryStatus(expiresAt) {
  if (!expiresAt) return null
  const daysLeft = (new Date(expiresAt) - Date.now()) / 86400000
  if (daysLeft <= 0) return null
  if (daysLeft < 3)  return 'red'
  if (daysLeft < 7)  return 'yellow'
  return null
}

function extractSnippet(content = "") {
  return content
    .replace(/\*\*/g, "")
    .replace(/#{1,6}\s/g, "")
    .replace(/`{1,3}/g, "")
    .replace(/\n+/g, " ")
    .trim()
    .slice(0, 160)
}

